
//////////////////////////////// main code

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, X, AlertTriangle, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { uploadSWImportChangeList, getCombinedSWChangeListsForImport, deleteSWImportChangeList, cacheManager, getLatestSWChangeListForVerification } from '../../services/apiService';
import { useSWAssessment } from './SWAssessmentContext';
import { decodedToken } from '../../utils/auth';
import Swal from 'sweetalert2';

const ImportSWChangeList = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [userUploads, setUserUploads] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('info');
  const [modalDetails, setModalDetails] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const decoded = decodedToken();
  const userEmail = decoded?.email || '';
  const userType = decoded?.userType || '';
  const refreshTrigger = location.state?.refreshKey;

  const {
    currentTab,
    setCurrentTab,
    setChangeList,
    clearTabData,
    getTabData,
    invalidateSWUploadData
  } = useSWAssessment();
 

  // ====================== TABS & SUB-TYPES ======================
  const tabs = [
    { id: 'TV', label: 'TV' },
    { id: 'MNT', label: 'MNT' },
    { id: 'PD', label: 'PD' },
    { id: 'AVA', label: 'AVA' },
  ];

  const subProductTypes = {
    PD: [
      { id: 'PD_Signage & IWB', label: 'PD_Signage & IWB' },
      { id: 'PD_RGB LED', label: 'PD_RGB LED' },
      { id: 'PD_AIO', label: 'PD_AIO' },
      { id: 'PD_EPD', label: 'PD_EPD' }
    ],
    AVA: [
      { id: 'AVA_Audio', label: 'AVA_Audio' },
      { id: 'AVA_Headphone', label: 'AVA_Headphone' }
    ]
  };
  //const [selectedSubType, setSelectedSubType] = useState({});

const [selectedSubType, setSelectedSubType] = useState(() => {
   const incomingSubType = location.state?.subType;
   if (incomingSubType) {
     const baseTab = incomingSubType.includes('_')
       ? incomingSubType.split('_')[0]
       : incomingSubType;
     if (subProductTypes[baseTab]) {
       return { [baseTab]: incomingSubType };
     }
   }
   return {};
 });

  const [showSubTypeDropdown, setShowSubTypeDropdown] = useState({});

  const effectiveProductType = useMemo(() => {
    if (subProductTypes[currentTab]) {
      return selectedSubType[currentTab] || subProductTypes[currentTab][0].id;
    }
    return currentTab;
  }, [currentTab, selectedSubType]);

  // ====================== TRUNCATE FILENAME ======================
  const truncateFilename = (filename, maxLength = 30) => {
    if (!filename || filename.length <= maxLength) return filename;
    const extension = filename.split('.').pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
    return `${truncatedName}...${extension}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setUploadedFile(null);
      setDefaultTemplate(null);
      setUserUploads([]);
      setSelectedTemplate(null);
      setDownloadUrl(null);
      setSelectedUploadId(null);
      setLoading(false);
      setError(null);
      setShowModal(false);
      setModalMessage('');
      setModalType('info');
      setModalDetails(null);
    };
  }, []);

  // Handle incoming navigation state (returnTab + subType)
  useEffect(() => {
    const { state } = location;
    if (!state) return;

    let shouldRefetch = false;

    // Set tab if coming back from import
    if (state.returnTab && state.returnTab !== currentTab) {
      setCurrentTab(state.returnTab);
      shouldRefetch = true;
    }

    if (state.subType) {
      console.log('Received sub-type from navigation:', state.subType);
      const baseTab = state.subType.includes('_')
        ? state.subType.split('_')[0]
        : state.subType;

      if (subProductTypes[baseTab]) {
        setSelectedSubType(prev => ({ ...prev, [baseTab]: state.subType }));
        shouldRefetch = true;
      }
    }
  }, [location]);

 const effectiveProductTypeRef = useRef(effectiveProductType);
 useEffect(() => {
   effectiveProductTypeRef.current = effectiveProductType;
 }, [effectiveProductType]);

// const fetchChangeLists = async (tabId) => {
//   if (!userEmail || !userType) {
//     setError('User email and type not found. Please log in again.');
//     return;
//   }

//   const productTypeToFetch =
//     selectedSubType[tabId] ||
//     (subProductTypes[tabId]
//       ? subProductTypes[tabId][0].id
//       : tabId);

//   console.log('Fetching SW change lists for:', productTypeToFetch);

//   setLoading(true);
//   setError(null);

//   try {
//     const cleanUserType = userType.trim();

//     // IMPORTANT:
//     // Always invalidate cache before fetching so latest
//     // default template is shown even if id is unchanged.
//     cacheManager.invalidateSWProduct(
//       productTypeToFetch,
//       userEmail
//     );

//     const response = await getCombinedSWChangeListsForImport(
//       productTypeToFetch,
//       userEmail,
//       cleanUserType
//     );

//     console.log('Combined SW Response:', response);


  const fetchChangeLists = async (tabId) => {
    if (!userEmail || !userType) {
      setError('User email and type not found. Please log in again.');
      return;
    }

    const productTypeToFetch =
      selectedSubType[tabId] ||
      (subProductTypes[tabId] ? subProductTypes[tabId][0].id : tabId);

    console.log('Fetching SW change lists for:', productTypeToFetch);

    setLoading(true);
    setError(null);

    try {
      const cleanUserType = userType.trim();

      cacheManager.invalidateSWProduct(productTypeToFetch, userEmail);

      const response = await getCombinedSWChangeListsForImport(
        productTypeToFetch,
        userEmail,
        cleanUserType
      );

     if (productTypeToFetch !== effectiveProductTypeRef.current) {
       console.log(
         'Ignoring stale fetch response for',
         productTypeToFetch,
         '— current subtype is now',
         effectiveProductTypeRef.current
       );
       return;
     }
     console.log('Combined SW Response:', response);

    let fetchedDefault = null;
    let fetchedUploads = [];

    if (response?.success) {

      if (response.defaultTemplate) {
        fetchedDefault = response.defaultTemplate;
        setDefaultTemplate(fetchedDefault);
      } else {
        setDefaultTemplate(null);
      }

      if (response.userUploads?.length > 0) {
        fetchedUploads = response.userUploads;
        setUserUploads(fetchedUploads);
      } else {
        setUserUploads([]);
      }

    } else {

      setDefaultTemplate(null);
      setUserUploads([]);
      setDownloadUrl(null);
      setSelectedTemplate(null);
      setSelectedUploadId(null);

      return;
    }

    // -------------------------------------------------
    // Determine currently active file from context
    // -------------------------------------------------

    const currentTabData = getTabData(tabId);

    console.log('IMPORT PAGE currentTabData:', currentTabData);

    const isDefaultSelected =
      currentTabData?.dataSource === 'default' ||
      location.state?.forceDefaultTemplate === true;

    const activeId = isDefaultSelected
      ? null
      : currentTabData?.changeListId || null;

    console.log('Current Tab Data:', currentTabData);
    console.log('isDefaultSelected:', isDefaultSelected);
    console.log('Active Id:', activeId);

    setSelectedUploadId(activeId);

    // -------------------------------------------------
    // Default Template Active
    // -------------------------------------------------

    if (isDefaultSelected) {

      console.log('Default template is active');

      setSelectedTemplate('default');

      if (fetchedDefault?.s3Url) {
        setDownloadUrl(fetchedDefault.s3Url);
      } else {
        setDownloadUrl(null);
      }

      return;
    }

      if (activeId) {
  const activeUpload =
    fetchedUploads.find(
      upload =>
        String(upload.id) ===
        String(activeId)
    );

  // Active upload still exists
  
  if (activeUpload) {
    console.log(
      'Active upload found:',
      activeUpload.filename
    );

    setSelectedUploadId(
      String(activeUpload.id)
    );

    setSelectedTemplate(
      activeUpload.id
    );

    setDownloadUrl(
      activeUpload.s3Url
    );

  } else {
 

    const latestRemainingUpload =
      fetchedUploads.length > 0
        ? fetchedUploads[0]
        : null;

    if (latestRemainingUpload) {
      console.log(
        'Previous active upload deleted. ' +
        'Selecting latest remaining upload:',
        latestRemainingUpload.filename
      );

      setSelectedUploadId(
        String(latestRemainingUpload.id)
      );

      setSelectedTemplate(
        latestRemainingUpload.id
      );

      setDownloadUrl(
        latestRemainingUpload.s3Url
      );

      // Keep context synchronized
      setChangeList(
        effectiveProductType,
        String(latestRemainingUpload.id),
        null,
        {},
        'import'
      );

    } else if (fetchedDefault) {
      console.log(
        'No uploads remaining. Falling back to default.'
      );

      setSelectedUploadId(null);

      setSelectedTemplate(
        'default'
      );

      setDownloadUrl(
        fetchedDefault.s3Url
      );

    } else {
      setSelectedUploadId(null);
      setSelectedTemplate(null);
      setDownloadUrl(null);
    }
  }
    } else {

  const latestUpload =
    fetchedUploads.length > 0
      ? fetchedUploads[0]
      : null;

  if (latestUpload) {
    console.log(
      'No active selection. Using latest upload:',
      latestUpload.filename
    );
    setSelectedUploadId(
      String(latestUpload.id)
    );
    setSelectedTemplate(
      latestUpload.id
    );
    setDownloadUrl(
      latestUpload.s3Url
    );

  } else if (fetchedDefault) {
    console.log(
      'No uploads. Using default template.'
    );

    setSelectedUploadId(null);

    setSelectedTemplate(
      'default'
    );

    setDownloadUrl(
      fetchedDefault.s3Url
    );

  } else {
    setSelectedUploadId(null);
    setSelectedTemplate(null);
    setDownloadUrl(null);
  }
}

  } catch (err) {

    console.error('Failed to load SW templates:', err);

    setError('Failed to load templates.');

    setDefaultTemplate(null);
    setUserUploads([]);
    setDownloadUrl(null);
    setSelectedTemplate(null);
    setSelectedUploadId(null);

  } finally {

    setLoading(false);
  }
};
// useEffect(() => {
//   if (currentTab && userEmail && userType) {
//     fetchChangeLists(currentTab);
//   }
// }, [
//   currentTab,
//   selectedSubType,
//   userEmail,
//   userType,
//   location.state?.refreshKey
// ]);

useEffect(() => {
  if (
    effectiveProductType &&
    userEmail &&
    userType
  ) {
    console.log('FETCH TEMPLATE DEBUG:', {
      currentTab,
      selectedSubType: selectedSubType[currentTab],
      effectiveProductType
    });

    fetchChangeLists(effectiveProductType);
  }
}, [
  effectiveProductType,
  userEmail,
  userType,
  location.state?.refreshKey
]);

  // ====================== HANDLERS ======================
  const handleBackClick = () => {
    navigate('/sw-change', {
      state: {
        activeTab: currentTab,
        returnFromImport: true,
        subType: effectiveProductType
      },
      replace: true
    });
  };

  const handleTemplateSelect = (template, type) => {
    setSelectedTemplate(type);
    setDownloadUrl(template.s3Url);
    console.log('Template selected only:', template);
  };

  const handleDownloadTemplate = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else {
      alert('No template available to download.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) setUploadedFile(file);
  };

  const handleUploadClick = () => {
    document.getElementById('sw-file-upload').click();
  };

  const handleTabChange = (tabId) => {
    if (tabId === currentTab) return;

    setUploadedFile(null);
    setDownloadUrl(null);
    setSelectedTemplate(null);
    setDefaultTemplate(null);
    setUserUploads([]);
    setSelectedUploadId(null);
    setCurrentTab(tabId);

    if (subProductTypes[tabId]) {
      setShowSubTypeDropdown(prev => ({ ...prev, [tabId]: true }));
    }
  };

  // const handleSubTypeChange = (tabId, subTypeId) => {
  //   setSelectedSubType(prev => ({ ...prev, [tabId]: subTypeId }));
  //   setShowSubTypeDropdown(prev => ({ ...prev, [tabId]: false }));
  //   setDefaultTemplate(null);
  //   setUserUploads([]);
  //   setDownloadUrl(null);
  //   setSelectedTemplate(null);
  // };
 const handleSubTypeChange = (
   tabId,
   subTypeId
) => {
   console.log(
     'SUBTYPE CHANGE DEBUG:',
     {
       tabId,
       previousSubType:
         selectedSubType[tabId],
       newSubType:
         subTypeId
     }
   );
   // Close dropdown
   setShowSubTypeDropdown(prev => ({
     ...prev,
     [tabId]: false
   }));

   // Clear UI state belonging to previous subtype

   setUploadedFile(null);
   setDefaultTemplate(null);
   setUserUploads([]);
   setDownloadUrl(null);
   setSelectedTemplate(null);
   setSelectedUploadId(null);
   setError(null);
   setSelectedSubType(prev => ({
     ...prev,
     [tabId]: subTypeId
   }));
  fetchChangeLists(subTypeId);
 };

  const handleSubmit = async () => {
  if (!uploadedFile) return;
  if (!userEmail || !userType) {
    setError('User email and type not found. Please log in again.');
    return;
  }

  setLoading(true);
  setError(null);
  setModalDetails(null);

  const formData = new FormData();
  formData.append('file', uploadedFile);
  formData.append('productType', effectiveProductType);

  try {
    const response = await uploadSWImportChangeList(
      formData,
      userEmail,
      userType
    );

    console.log('SW Upload Response:', response);

    if (response.success === false) {
      setModalType('error');
      setModalMessage(response.message || 'Upload failed');
      setShowModal(true);
      return;
    }

    if (response.isDuplicate) {
      setModalType('duplicate');
      setModalMessage(
        'The uploaded file is identical to the current version. No changes detected.'
      );
      setShowModal(true);

      cacheManager.invalidateSWProduct(
        effectiveProductType,
        userEmail
      );
      //await fetchChangeLists(currentTab);
      await fetchChangeLists(effectiveProductType);
      setUploadedFile(null);
      return;
    }
    setModalType('info');
    setModalMessage('SW Change list uploaded successfully!');
    setShowModal(true);

    // IMPORTANT
    cacheManager.invalidateSWProduct(
      effectiveProductType,
      userEmail
    );

    //clearTabData(currentTab);
    clearTabData(effectiveProductType);

    //await fetchChangeLists(currentTab);
    await fetchChangeLists(effectiveProductType);

    if (response.s3Url) {
      setDownloadUrl(response.s3Url);
    }
    // if (response.id) {
    //   setSelectedTemplate(response.id);
    //   setSelectedUploadId(response.id);
    // }
    if (response.id) {
  setSelectedTemplate(response.id);
  setSelectedUploadId(null);
}

    setUploadedFile(null);

    setTimeout(() => {
//       navigate('/sw-change', {
//   state: {
//     activeTab: currentTab,
//     changeListId: response.id,
//     returnFromImport: true,
//     newUpload: true,
//     refreshKey: Date.now(),
//     subType: effectiveProductType
//   }
// });

navigate('/sw-change', {
  state: {
    activeTab: currentTab,
    changeListId: response.id,
    selectedSWUploadId: null,
    returnFromImport: true,
    newUpload: true,
    refreshKey: Date.now(),
    subType: effectiveProductType
  }
});
    }, 1200);

  } catch (err) {
    console.error('SW Upload error:', err);
    const data = err.response?.data;
    const errorMessage =
      data?.message ||
      data?.error ||
      'Failed to upload file';
    setModalType('error');
    setModalMessage(errorMessage);
    setShowModal(true);
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  } finally {
    setLoading(false);
  }
};

//  const handleSelectUpload = async (
//   upload,
//   isDefaultTemplate = false
// ) => {
//   try {
//     setLoading(true);

//     clearTabData(currentTab);

//     if (isDefaultTemplate) {

//       // Mark default template as active in context
//       setChangeList(
//         currentTab,
//         null,
//         null,
//         {},
//         'default'
//       );

//       setSelectedUploadId(null);
//       setSelectedTemplate('default');

//       // navigate('/sw-change', {
//       //   state: {
//       //     activeTab: currentTab,
//       //     subType: effectiveProductType,
//       //     forceDefaultTemplate: true,
//       //     refreshKey: Date.now()
//       //   }
//       // });

// //       navigate('/sw-change', {
// //   state: {
// //     activeTab: currentTab,
// //     subType: effectiveProductType,
// //     returnFromImport: true,
// //     forceDefaultTemplate: true,
// //     refreshKey: Date.now()
// //   }
// // });
// if (isDefaultTemplate) {

//   // Bust cache so latestForVerification never returns 304
//   cacheManager.invalidateSWProduct(effectiveProductType, userEmail);

//   setChangeList(currentTab, null, null, {}, 'default');
//   setSelectedUploadId(null);
//   setSelectedTemplate('default');

//   navigate('/sw-change', {
//     state: {
//       activeTab: currentTab,
//       subType: effectiveProductType,
//       returnFromImport: true,
//       forceDefaultTemplate: true,
//       refreshKey: Date.now()    // ensures effect re-runs
//     }
//   });
//   return;
// }

//       return;
//     }

//     setSelectedUploadId(upload.id);

//     setChangeList(
//       currentTab,
//       upload.id,
//       null,
//       {},
//       'import'
//     );

//     navigate('/sw-change', {
//       state: {
//         activeTab: currentTab,
//         changeListId: upload.id,
//         returnFromImport: true,
//         subType: effectiveProductType,
//         refreshKey: Date.now()
//       }
//     });

//   } finally {
//     setLoading(false);
//   }
// };
const handleSelectUpload = async (
  upload,
  isDefaultTemplate = false
) => {
  try {
    setLoading(true);
    clearTabData(effectiveProductType);


    if (isDefaultTemplate) {
      cacheManager.invalidateSWProduct(
        effectiveProductType,
        userEmail
      );

      // Reload exact subtype/product
      await fetchChangeLists(
        effectiveProductType
      );
      // Store context using exact subtype/product key
      setChangeList(
        effectiveProductType,
        null,
        null,
        {},
        'default'
      );

      setSelectedUploadId(null);
      setSelectedTemplate('default');

      if (defaultTemplate?.s3Url) {
        setDownloadUrl(
          defaultTemplate.s3Url
        );
      } else {
        setDownloadUrl(null);
      }

      navigate('/sw-change', {
        state: {
          activeTab: currentTab,
          subType: effectiveProductType,
          returnFromImport: true,
          forceDefaultTemplate: true,
          selectedSWUploadId: null,
          refreshKey: Date.now()
        }
      });

      return;
    }

    // ==================================================
    // USER UPLOAD EXPLICITLY SELECTED
    // ==================================================
    const uploadId =
      upload?.id || upload?._id;

    if (!uploadId) {
      console.error(
        'SW upload ID missing:',
        upload
      );

      return;
    }

    const normalizedUploadId =
      String(uploadId);

    console.log(
      'Explicitly selected SW upload:',
      {
        normalizedUploadId,
        currentTab,
        effectiveProductType
      }
    );

    // Store selected upload ID
    setSelectedUploadId(
      normalizedUploadId
    );

    setChangeList(
      effectiveProductType,
      normalizedUploadId,
      null,
      {},
      'import'
    );

    // Update download button immediately
    setSelectedTemplate(
      normalizedUploadId
    );

    if (upload?.s3Url) {
      setDownloadUrl(upload.s3Url);
    }

    navigate('/sw-change', {
      state: {
        activeTab: currentTab,
        activeDataKey:
          effectiveProductType,
        changeListId:
          normalizedUploadId,
        selectedSWUploadId:
          normalizedUploadId,
        explicitSWUploadSelection:
          true,
        returnFromImport: true,
        subType: effectiveProductType,
        refreshKey: Date.now()
      }
    });

  } catch (error) {
    console.error(
      'Failed to select SW upload:',
      error
    );

    setError(
      'Failed to select uploaded file.'
    );

  } finally {
    setLoading(false);
  }
};

//const currentTabData = getTabData(currentTab);
const currentTabData = getTabData(effectiveProductType);

  // const handleRemoveUpload = async (uploadId, e) => {
  //   e.stopPropagation();

  //   if (!userEmail) {
  //     Swal.fire({
  //       icon: 'error',
  //       title: 'Error',
  //       text: 'User email not found. Please log in again.',
  //       confirmButtonColor: '#3085d6',
  //     });
  //     return;
  //   }

  //   const result = await Swal.fire({
  //     title: 'Are you sure?',
  //     text: 'You want to delete this uploaded file?',
  //     icon: 'warning',
  //     showCancelButton: true,
  //     confirmButtonColor: 'green',
  //     cancelButtonColor: 'red',
  //     confirmButtonText: 'Yes, delete it!',
  //     cancelButtonText: 'Cancel',
  //     reverseButtons: true,
  //   });

  //   if (!result.isConfirmed) {
  //     return;
  //   }
  //   setLoading(true);
  //   setError(null);

  //   try {
  //     await deleteSWImportChangeList(uploadId, userEmail);
  //     cacheManager.invalidateSWProduct(effectiveProductType, userEmail);
  //     setSelectedUploadId(null);
  //     await fetchChangeLists(currentTab);
  //     // Success Message
  //     Swal.fire({
  //       icon: 'success',
  //       title: 'Deleted!',
  //       text: 'Upload has been deleted successfully.',
  //       confirmButtonColor: '#3085d6',
  //       timer: 2000,
  //       timerProgressBar: true,
  //     });

  //   } catch (err) {
  //     console.error('SW Delete error:', err);

  //     const errorMsg =
  //       err.response?.data?.error ||
  //       err.response?.data?.message ||
  //       'Failed to delete upload.';

  //     Swal.fire({
  //       icon: 'error',
  //       title: 'Delete Failed',
  //       text: errorMsg,
  //       confirmButtonColor: '#3085d6',
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

const handleRemoveUpload = async (uploadId, e) => {
  e.stopPropagation();

  if (!userEmail) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'User email not found. Please log in again.',
      confirmButtonColor: '#3085d6'
    });
    return;
  }

  const result = await Swal.fire({
    title: 'Are you sure?',
    text: 'You want to delete this uploaded file?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: 'green',
    cancelButtonColor: 'red',
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel',
    reverseButtons: true
  });

  if (!result.isConfirmed) {
    return;
  }
  setLoading(true);
  setError(null);

  try {
    const deletedId = String(uploadId);

    // Remember currently selected upload
    const previouslySelectedId =
      selectedUploadId
        ? String(selectedUploadId)
        : null;

    console.log('DELETE UPLOAD DEBUG:', {
      deletedId,
      previouslySelectedId,
      currentTab,
      effectiveProductType
    });

    // --------------------------------------------------
    // 1. Delete file from backend
    // --------------------------------------------------
    await deleteSWImportChangeList(
      deletedId,
      userEmail
    );

    // 2. Clear stale React Context data
  invalidateSWUploadData(
  effectiveProductType
);

    // --------------------------------------------------
    // 2. Clear cache for exact subtype/product
    // --------------------------------------------------
    cacheManager.invalidateSWProduct(
      effectiveProductType,
      userEmail
    );

    // --------------------------------------------------
    // 3. Check whether deleted file was selected
    // --------------------------------------------------
    const deletedSelectedUpload =
      previouslySelectedId === deletedId;

    if (deletedSelectedUpload) {
      console.log(
        'Deleted upload was the currently selected upload.'
      );

      setSelectedUploadId(null);
    } else {
      console.log(
        'Deleted upload was not the selected upload.'
      );

      // Keep current selection
      setSelectedUploadId(
        previouslySelectedId
      );
    }

    console.log(
      'Reloading remaining uploads for:',
      effectiveProductType
    );

    await fetchChangeLists(
      effectiveProductType
    );

    Swal.fire({
      icon: 'success',
      title: 'Deleted!',
      text: 'Upload has been deleted successfully.',
      confirmButtonColor: '#3085d6',
      timer: 2000,
      timerProgressBar: true
    });

  } catch (err) {
    console.error(
      'SW Delete error:',
      err
    );

    const errorMsg =
      err.response?.data?.error ||
      err.response?.data?.message ||
      'Failed to delete upload.';

    setError(errorMsg);
    Swal.fire({
      icon: 'error',
      title: 'Delete Failed',
      text: errorMsg,
      confirmButtonColor: '#d33'
    });
  } finally {
    setLoading(false);
  }
};

  const closeModal = () => {
    setShowModal(false);
    setModalMessage('');
    setModalDetails(null);
  };

  const getModalColor = () => {
    switch (modalType) {
      case 'duplicate': return 'bg-yellow-900/30 border-yellow-500';
      case 'error': return 'bg-red-900/30 border-red-500';
      default: return 'bg-blue-900/30 border-blue-500';
    }
  };

  const getModalIcon = () => modalType === 'error'
    ? <AlertTriangle className="w-8 h-8 text-red-400" />
    : null;

    const isDefaultActive =
    currentTabData?.dataSource === 'default';

    const activeUploadId =
    selectedUploadId ||
    (currentTabData?.dataSource === 'import'
      ? currentTabData?.changeListId
      : null);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Modal - unchanged */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${getModalColor()} border p-6 rounded-lg max-w-md w-full shadow-2xl`}>
            <div className="flex items-start gap-3 mb-4">
              {getModalIcon()}
              <h3 className="text-lg font-semibold text-white">
                {modalType === 'duplicate' ? 'Duplicate Detected' : modalType === 'error' ? 'Invalid File' : 'Information'}
              </h3>
            </div>
            <p className="text-sm text-gray-300 whitespace-pre-line mb-4">{modalMessage}</p>
            {modalDetails && (
              <div className="mt-4 p-3 bg-gray-900/50 rounded border border-gray-700 text-xs">
                <div className="mb-2">
                  <span className="font-semibold text-gray-300">Expected sheet:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {modalDetails.expected.map(sheet => (
                      <span key={sheet} className="px-2 py-1 bg-green-900/30 text-green-300 rounded">{sheet}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="font-semibold text-gray-300">Found in your file:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {modalDetails.found.map(sheet => (
                      <span key={sheet} className="px-2 py-1 bg-red-900/30 text-red-300 rounded">{sheet}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={closeModal} className={`px-6 py-2 rounded-md font-medium transition-all ${modalType === 'duplicate' ? 'bg-yellow-600 hover:bg-yellow-500' : modalType === 'error' ? 'bg-red-600 hover:bg-red-500' : 'bg-blue-600 hover:bg-blue-500'} text-white`}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-20 bg-black/95 backdrop-blur-sm border-b border-gray-600">
        <div className="flex items-center px-4 py-3 sm:px-6">
          <button onClick={handleBackClick} className="flex items-center justify-center w-10 h-10 -ml-2 mr-2 rounded-lg hover:bg-gray-800 active:bg-gray-700 transition-colors touch-manipulation">
            <ArrowLeft className="w-5 h-5 text-white hover:text-white" />
          </button>
          <h1 className="text-base font-medium sm:text-lg">
            SW Change Assessment — Import for {currentTab}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 sm:px-6 sm:py-8 max-w-4xl mx-auto">
        <div className="sm:mb-3">
          <h2 className="text-xl font-normal text-white sm:text-3xl md:text-3xl sm:mb-3">
            Import Change List for {currentTab}
          </h2>
        </div>

        {/* Tabs */}
        <div className="sm:mb-4 mb-3">
          <div className="flex border-b border-gray-700 overflow-x-auto">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-shrink-0 px-6 py-4 text-sm font-medium border-b-2 transition-all touch-manipulation
                  ${currentTab === tab.id ? 'border-blue-500 text-white bg-blue-500/10' : 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/50'}
                  ${index < tabs.length - 1 ? 'mr-2 sm:mr-8' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-8">

          {/* Sub-type Dropdown */}
          {subProductTypes[currentTab] && (
            <div className="sm:mb-3">
              <div className="relative inline-block w-64">
                <button
                  onClick={() => setShowSubTypeDropdown(prev => ({ ...prev, [currentTab]: !prev[currentTab] }))}
                  className="w-full flex items-center justify-between px-4 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <span className="text-sm">
                    {subProductTypes[currentTab]?.find(st => st.id === selectedSubType[currentTab])?.label || 'Select Type'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showSubTypeDropdown[currentTab] ? 'rotate-180' : ''}`} />
                </button>

                {showSubTypeDropdown[currentTab] && (
                  <div className="absolute z-30 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-60 overflow-auto">
                    {subProductTypes[currentTab].map(subType => (
                      <button
                        key={subType.id}
                        onClick={() => handleSubTypeChange(currentTab, subType.id)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 transition-colors ${selectedSubType[currentTab] === subType.id ? 'bg-blue-600 text-white' : 'text-gray-300'
                          }`}
                      >
                        {subType.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Download Template */}
          <div>
            {downloadUrl && selectedTemplate && (
              <div className="text-sm text-gray-300 mb-3">
                <span className="font-medium">
                  {selectedTemplate === 'default'
                    ? truncateFilename(defaultTemplate?.filename || 'Default Template', 50)
                    : truncateFilename(
                      userUploads.find(u => u.id === selectedTemplate)?.filename || 'Template', 50
                    )
                  }
                </span>
              </div>
            )}
            <button
              onClick={handleDownloadTemplate}
              disabled={!downloadUrl}
              className={`px-8 sm:py-4 py-2 rounded-xl text-white font-medium transition-all shadow-lg hover:shadow-xl ${downloadUrl
                  ? 'bg-blue-600/80 hover:bg-blue-600'
                  : 'bg-gray-600/50 cursor-not-allowed'
                }`}
            >
              Download Current Template
            </button>
            {!downloadUrl && !loading && (
              <p className="text-sm text-gray-400 mt-2">No template available</p>
            )}
          </div>

          {/* Upload Section */}
          <div className="space-y-4">
            <button
              onClick={handleUploadClick}
              className="px-8 sm:py-4 py-2  bg-gray-700/80 hover:bg-gray-700 rounded-xl text-white font-medium transition-all shadow-lg hover:shadow-xl"
            >
              Upload New File
            </button>
            <input
              id="sw-file-upload"
              type="file"
              accept=".xlsx,.xls,.xlsm"
              onChange={handleFileUpload}
              className="hidden"
            />
            <div className="w-full max-w-2xl">
              <div className={`${uploadedFile ? "min-h-[80px] p-6" : "min-h-[50px] p-3"} bg-gray-800/50 border-2 border-dashed border-gray-600 rounded-xl flex items-center justify-center`}>
                {uploadedFile ? (
                  <div className="text-center">
                    <div className="text-white font-medium" title={uploadedFile.name}>
                      {truncateFilename(uploadedFile.name)}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">No file selected</div>
                )}
              </div>
            </div>
          </div>

          {/* Default Template */}
          <div className="space-y-4">
            <h3 className="text-xl font-medium text-white">Default Template</h3>

            {loading ? (
              <p className="text-gray-400">Loading...</p>

            ) : error ? (
              <p className="text-red-400">{error}</p>

            ) : !defaultTemplate ? (
              <p className="text-gray-400">
                No default template available for {currentTab}.
              </p>

            ) : (

              <div
                onClick={() => handleTemplateSelect(defaultTemplate, 'default')}
                className={`flex justify-between items-center p-4 rounded-lg border cursor-pointer transition-all  
                   ${
                      isDefaultActive
                        ? 'bg-blue-950/40 border-blue-800'
                        : selectedTemplate === 'default'
                        ? 'bg-blue-900/20 border-blue-500'
                        : 'bg-gray-800/50 border-gray-600 hover:border-gray-500'
                    }
                  `}
              >

                <div className="flex-1 min-w-0">
                  <p
                    className="text-white font-medium truncate mb-2"
                    title={defaultTemplate.filename}
                  >
                    {defaultTemplate.filename}
                  </p>

                  <p className="text-gray-400 text-sm">
                    {new Date(defaultTemplate.uploadDate).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                <div className="flex items-center gap-2 ml-4">

                {isDefaultActive ? (
                  <>
                    <div className="w-3 h-3 rounded-full mr-2 bg-blue-800" />

                    <span className="px-3 py-1 rounded-lg bg-green-700 text-white text-xs font-semibold">
                      Current
                    </span>
                  </>
                ) : (
                  <>
                    <div className="w-3 h-3 rounded-full mr-2 bg-blue-500" />

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectUpload(defaultTemplate, true);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      Use This
                    </button>
                  </>
                )}
                </div>

              </div>
            )}
          </div>

          {/* My Uploads */}
          <div className="space-y-4">
            <h3 className="text-xl font-medium text-white">My Uploads</h3>

            {loading ? (
              <p className="text-gray-400">Loading...</p>

            ) : userUploads.length === 0 ? (
              <p className="text-gray-400">
                No uploads found for {currentTab}.
              </p>

            ) : (

              <div className="space-y-2 max-h-96 overflow-y-auto pr-2 custom-scrollbar">

                {[...userUploads]
                  .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
                  .map((upload) => (

                    <div
                      key={upload.id}
                      onClick={() => handleTemplateSelect(upload, upload.id)}
                      className={`flex justify-between items-center p-4 rounded-lg border cursor-pointer transition-all
                                          ${
                      String(activeUploadId) === String(upload.id)
                        ? 'bg-blue-950/40 border-blue-800'
                        : selectedTemplate === upload.id
                        ? 'bg-blue-900/20 border-blue-500'
                        : 'bg-gray-800/50 border-gray-600 hover:border-gray-500'
                    }
                  `}
                    >

                      <div className="flex-1 min-w-0">
                        <p
                          className="text-white font-medium truncate mb-2"
                          title={upload.filename}
                        >
                          {upload.filename}
                        </p>

                        <p className="text-gray-400 text-sm">
                          {new Date(upload.uploadDate).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 ml-4">

                      {String(activeUploadId) === String(upload.id) ? (
                      <>
                        <div className="w-3 h-3 rounded-full mr-2 bg-blue-800" />

                        <span className="px-3 py-1 rounded-lg bg-green-700 text-white text-xs font-semibold">
                          Current
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-3 h-3 rounded-full mr-2 bg-blue-500" />

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectUpload(upload);
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-medium transition-colors"
                        >
                          Use This
                        </button>
                      </>
                    )}

                        <button
                          onClick={(e) => handleRemoveUpload(upload.id, e)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                          aria-label="Remove upload"
                        >
                          <X size={20} />
                        </button>

                      </div>

                    </div>
                  ))}

              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-4 pt-2 justify-end">
            <button onClick={handleBackClick} className="w-full sm:w-auto px-8 sm:py-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-xl text-white font-medium transition-colors">
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={!uploadedFile || loading}
              className={`w-full sm:w-auto px-8 sm:py-3 py-2 rounded-xl font-medium transition-all ${uploadedFile && !loading ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600/50 text-white/70 cursor-not-allowed'
                }`}
            >
              {loading ? 'Uploading...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportSWChangeList;